// eslint-disable-next-line eag/match-named-export
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {
  CheckVinAvailabilityApiArg,
  CheckVinAvailabilityApiResponse,
  GetCategoriesApiArg,
  GetCategoriesApiResponse,
  GetModelsInfoApiV1HealthModelsGetApiArg,
  GetModelsInfoApiV1HealthModelsGetApiResponse,
  HealthCheckApiV1HealthGetApiArg,
  HealthCheckApiV1HealthGetApiResponse,
  PredictByVinApiArg,
  PredictByVinApiResponse,
  PredictByVinRulApiArg,
  PredictByVinRulApiResponse,
  PredictStatelessApiArg,
  PredictStatelessApiResponse,
  PredictStatelessRulApiArg,
  PredictStatelessRulApiResponse,
} from './schemas';

/**
 * Resolved at build time via `DefinePlugin` in `apps/mira/rspack.config.ts`,
 * which inlines `process.env.MIRA_API_URL` as a literal string. Falls back
 * to a local-dev default when the env var is unset (e.g. unit tests where
 * DefinePlugin never ran but Node provides `process` natively).
 *
 * The literal-style access pattern (`process.env.MIRA_API_URL`) is required
 * for DefinePlugin to find and replace the token — destructuring or
 * optional chaining would defeat the substitution.
 */
const FALLBACK_BASE_URL = 'http://localhost:8000';

const ENV_BASE_URL = process.env.MIRA_API_URL;
const RESOLVED_BASE_URL =
  typeof ENV_BASE_URL === 'string' && ENV_BASE_URL.length > 0 ? ENV_BASE_URL : FALLBACK_BASE_URL;

/**
 * RTK Query API for Mira
 */
export const miraApi = createApi({
  reducerPath: 'miraApi',
  baseQuery: fetchBaseQuery({baseUrl: RESOLVED_BASE_URL}),
  tagTypes: ['health', 'categories'],
  endpoints: (build) => ({
    healthCheckApiV1HealthGet: build.query<
      HealthCheckApiV1HealthGetApiResponse,
      HealthCheckApiV1HealthGetApiArg
    >({
      query: () => ({url: `/-/health-checks/liveness`}),
      providesTags: ['health'],
    }),
    getModelsInfoApiV1HealthModelsGet: build.query<
      GetModelsInfoApiV1HealthModelsGetApiResponse,
      GetModelsInfoApiV1HealthModelsGetApiArg
    >({
      query: () => ({url: `/api/v1/health/models`}),
      providesTags: ['health'],
    }),
    predictByVin: build.mutation<PredictByVinApiResponse, PredictByVinApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/predict/vin`,
        method: 'POST',
        body: queryArg.vinPredictionRequest,
      }),
    }),
    getCategories: build.query<GetCategoriesApiResponse, GetCategoriesApiArg>({
      query: () => ({url: `/api/v1/categories`}),
      providesTags: ['categories'],
    }),
    checkVinAvailability: build.query<CheckVinAvailabilityApiResponse, CheckVinAvailabilityApiArg>({
      query: (queryArg) => ({url: `/api/v1/vehicle/${queryArg.vin}/check`}),
    }),
    predictStateless: build.mutation<PredictStatelessApiResponse, PredictStatelessApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/predict`,
        method: 'POST',
        body: queryArg.statelessPredictionRequest,
      }),
    }),
    predictByVinRul: build.mutation<PredictByVinRulApiResponse, PredictByVinRulApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/predict/vin/rul`,
        method: 'POST',
        body: queryArg.vinRulRequest,
      }),
    }),
    predictStatelessRul: build.mutation<PredictStatelessRulApiResponse, PredictStatelessRulApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/predict/rul`,
        method: 'POST',
        body: queryArg.statelessRulRequest,
      }),
    }),
  }),
});

// Re-export types from schemas
export type {
  CategoriesResponse,
  CategoryId,
  CategoryInfo,
  CategoryPrediction,
  CheckVinAvailabilityApiArg,
  CheckVinAvailabilityApiResponse,
  DataCompleteness,
  GetCategoriesApiArg,
  GetCategoriesApiResponse,
  GetModelsInfoApiV1HealthModelsGetApiArg,
  GetModelsInfoApiV1HealthModelsGetApiResponse,
  HealthCheckApiV1HealthGetApiArg,
  HealthCheckApiV1HealthGetApiResponse,
  HealthResponse,
  HttpValidationError,
  ModelsInfoResponse,
  PredictByVinApiArg,
  PredictByVinApiResponse,
  PredictByVinRulApiArg,
  PredictByVinRulApiResponse,
  PredictStatelessApiArg,
  PredictStatelessApiResponse,
  PredictStatelessRulApiArg,
  PredictStatelessRulApiResponse,
  PredictionResponse,
  RulCategoryPrediction,
  RulPredictionResponse,
  StatelessPredictionRequest,
  StatelessPredictionResponse,
  StatelessRulRequest,
  ValidationError,
  VehicleData,
  VinCheckResponse,
  VinPredictionRequest,
  VinRulRequest,
} from './schemas';

export const {
  useHealthCheckApiV1HealthGetQuery,
  useGetModelsInfoApiV1HealthModelsGetQuery,
  usePredictByVinMutation,
  useGetCategoriesQuery,
  useCheckVinAvailabilityQuery,
  useLazyCheckVinAvailabilityQuery,
  usePredictStatelessMutation,
  usePredictByVinRulMutation,
  usePredictStatelessRulMutation,
} = miraApi;
